import React, { useState, useEffect } from "react";
import clsx from "clsx";

import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineOutlined from "@material-ui/icons/AddCircleOutlineOutlined";
import Select from "@material-ui/core/Select";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import SuccessCircle from "components/Icons/SuccessCircle";
import TrackConditionBlack from "components/Icons/TrackConditionsBlack";
import { getTimeAgo } from "utils/date-utils";

import useStyles from "./styles";
import { conditionsLabels, conditionsBySurfaceType } from "../constants";
import { getSurfaceType } from "../utils";

type Props = {
  course: racehorse360.ICourse;
  originalTrackCondition: racehorse360.TrackCondition;
  handleChangeTrackCondition: (
    courseId: string,
    newTrackConditionValue: racehorse360.TrackCondition
  ) => void;
  handleChangeNote: (courseId: string, newNoteValue: string) => void;
  clearNote: (courseId: string) => void;
  courseChanges: {
    isTrackConditionChanged: boolean;
    isNoteChanged: boolean;
  };
  addToOpenState: (id: string) => void;
  removeFromOpenState: (id: string) => void;
};

const Course = React.memo((props: Props) => {
  const {
    course,
    originalTrackCondition,
    handleChangeTrackCondition,
    handleChangeNote,
    clearNote,
    courseChanges,
    addToOpenState,
    removeFromOpenState
  } = props;

  const {
    id,
    name,
    surface,
    trackCondition,
    comment,
    previousTrackCondition,
    isOnlyCommentUpdated,
    updatedOn
  } = course;

  const [isFocus, setFocus] = useState<boolean>(false);
  const [textValue, setTextValue] = useState<string>(comment);

  const classes = useStyles();

  useEffect(() => {
    setTextValue(comment);
  }, [comment]);

  const handleChangeCondition = event => {
    handleChangeTrackCondition(id, event.target.value);
  };

  const handleChangeDescription = event => {
    if (event.target.value.length > 200) return;
    setTextValue(event.target.value);
  };

  const handleSaveNoteLocally = () => {
    handleChangeNote(id, textValue);
    setFocus(false);
    removeFromOpenState(id);
  };

  const handleCancel = () => {
    setTextValue(comment);
    setFocus(false);
    removeFromOpenState(id);
  };

  const handleTextareaFocus = () => {
    setFocus(true);
    addToOpenState(id);
  };

  const handleTextareaBlur = () => {
    if (textValue === comment) {
      setFocus(false);
      removeFromOpenState(id);
    }
  };

  const renderConditions = () => {
    const surfaceType = getSurfaceType(surface);
    const currentSurfaceTrackConditionsList =
      conditionsBySurfaceType[surfaceType];

    return currentSurfaceTrackConditionsList.map((it, i) => {
      return (
        <MenuItem
          key={i}
          value={it}
          className={clsx(
            classes.menuItem,
            it === trackCondition ? classes.selectedMenuItem : ""
          )}
        >
          {
            <>
              <div className={classes.menuItemText}>{conditionsLabels[it]}</div>
              {it === trackCondition && <SuccessCircle />}
            </>
          }
        </MenuItem>
      );
    });
  };

  const handleDeleteNote = () => {
    clearNote(id);
  };

  const getLastChanges = () => {
    if (!updatedOn) return null;

    if (isOnlyCommentUpdated) {
      return "Comment Update";
    }

    const from = conditionsLabels[previousTrackCondition] || "-";
    const to = conditionsLabels[originalTrackCondition] || "-";

    return `From ${from} to ${to}`;
  };

  return (
    <div className={classes.courseContainer}>
      <div className={classes.header}>
        <div className={classes.track}>
          <TrackConditionBlack className={classes.trackIcon} />
          <div className={classes.trackName}>{name}</div>
        </div>
        <div className={classes.lastUpdate}>
          <span className={classes.lastUpdatePrefix}>Last Updated</span>
          <span className={classes.lastUpdateValue}>{` ${
            getTimeAgo(updatedOn) || "-"
          }`}</span>
          <div className={classes.fromTo}>{getLastChanges()}</div>
        </div>
      </div>
      <div className={classes.conditionSelectionBlock}>
        <div className={classes.conditionTitle}>
          Current Track Condition
          {courseChanges.isTrackConditionChanged && (
            <span className={classes.hasChangesIndicator} />
          )}
        </div>
        <div className={classes.conditionSelect}>
          <Select
            className={classes.conditionSelector}
            labelId="conditionSelectLabel"
            id="conditionSelect"
            value={trackCondition ? trackCondition : ""}
            onChange={handleChangeCondition}
          >
            {renderConditions()}
          </Select>
        </div>
      </div>
      <div className={classes.conditionNote}>
        <div className={classes.conditionNoteHeader}>
          <div className={classes.conditionNoteTitle}>
            Note
            {courseChanges.isNoteChanged && (
              <span className={classes.hasChangesIndicator} />
            )}
          </div>
          <Button
            variant="outlined"
            className={classes.deleteNoteButton}
            onClick={handleDeleteNote}
            disabled={!comment.length || isFocus}
          >
            <DeleteIcon className={classes.deleteNoteButtonIcon} />
            <Typography className={classes.deleteNoteButtonText}>
              &nbsp;DELETE
            </Typography>
          </Button>
        </div>
        <div className={classes.conditionNoteBody}>
          <label>
            <TextareaAutosize
              className={clsx(classes.noteText, {
                [classes.noteTextFocus]: isFocus
              })}
              aria-label="empty textarea"
              minRows={4}
              value={textValue}
              onChange={handleChangeDescription}
              onFocus={handleTextareaFocus}
              onBlur={handleTextareaBlur}
            />

            {isFocus ? (
              <div className={classes.noteTextCounter}>
                {`${textValue?.length || 0} / 200`}
              </div>
            ) : null}

            {!isFocus && !textValue?.length && !courseChanges.isNoteChanged ? (
              <div className={classes.placeholder}>
                <AddCircleOutlineOutlined className={classes.placeholderIcon} />
                <div className={classes.placeholderText}>Add Note</div>
              </div>
            ) : null}
          </label>
        </div>
        {isFocus ? (
          <div className={classes.closeSaveButtonsContainer}>
            <Button className={classes.cancelButton} onClick={handleCancel}>
              <Typography className={classes.cancelButtonText}>
                &nbsp;CANCEL
              </Typography>
            </Button>
            <Button
              className={classes.saveButton}
              onClick={handleSaveNoteLocally}
              disabled={!textValue.trim().length && !course.comment.length}
            >
              <Typography className={classes.saveButtonText}>
                &nbsp;SAVE NOTE
              </Typography>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default Course;
